import { ContentContainer } from 'components/containers';
import { LoadingSpinner } from 'components/spinners';
import { FormattedUnitValue } from 'components/text';
import { useBusinessData } from 'features/dashboard/hooks';
import { useBoolean } from 'hooks';
import { FeatureIcon, FeatureIconName } from 'libs/icons';
import _ from 'lodash';
import { useMemo } from 'react';
import { ITurnoverExtended, IUserProfile } from 'types';
import { IUserTurnoverData } from 'types/business-data/IUserTurnoverData';
import { computePercentageTrend } from 'utils';

import { WidgetTurnoverDetails } from '../../turnover/WidgetTurnoverDetails';
import { FirstlineDataProperty } from '../FirstlineDataProperty';
import { useFirstlineHeaderI18n } from '../headers/useFirstlineHeaderI18n';

interface IFirstlineTableColumnValueProps {
  readonly userProfile: IUserProfile;
}

export function StructureSalesColumn(props: IFirstlineTableColumnValueProps) {
  return (
    <FirstlineTableSalesColumn
      value={props.userProfile.businessData?.turnoverFromStructure.current}
      userProfile={props.userProfile}
      businessDataPropertyName="turnoverFromStructure"
      icon={FeatureIconName.StructureSales}
      title={FirstlineDataProperty.StructureSales}
    />
  );
}

export function PersonalSalesColumn(props: {
  readonly userProfile: IUserProfile;
}) {
  return (
    <FirstlineTableSalesColumn
      value={props.userProfile.businessData?.turnoverFromSelf.current}
      userProfile={props.userProfile}
      businessDataPropertyName="turnoverFromSelf"
      icon={FeatureIconName.PersonalSales}
      title={FirstlineDataProperty.PersonalSales}
    />
  );
}

export function SixLevelSalesColumn(props: {
  readonly userProfile: IUserProfile;
}) {
  return (
    <FirstlineTableSalesColumn
      value={props.userProfile.businessData?.turnoverFromSixLevel.current}
      userProfile={props.userProfile}
      businessDataPropertyName="turnoverFromSixLevel"
      icon={FeatureIconName.SixLevelSales}
      title={FirstlineDataProperty.SixLevelSales}
    />
  );
}

export function OpenAutoshipVolumeColumn(props: {
  readonly userProfile: IUserProfile;
}) {
  const openAutoshipVolume =
    props.userProfile.businessData?.autoshipsOpenTurnover;

  if (_.isUndefined(openAutoshipVolume)) {
    return null;
  }

  return <FormattedUnitValue unit="points" value={openAutoshipVolume} />;
}

function FirstlineTableSalesColumn(props: {
  readonly value?: number;
  readonly userProfile: IUserProfile;
  readonly businessDataPropertyName: keyof IUserTurnoverData;
  readonly icon: FeatureIconName;
  readonly title: FirstlineDataProperty;
}) {
  const [isOpen, { setFalse: close, setTrue: open }] = useBoolean(false);
  const t = useFirstlineHeaderI18n();

  if (_.isUndefined(props.value)) {
    return <span></span>;
  }

  return (
    <>
      <button
        className="inline-block"
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          open();
        }}
      >
        <FormattedUnitValue unit="points" value={props.value} />
      </button>
      <ContentContainer
        onDismiss={close}
        open={isOpen}
        icon={
          <FeatureIcon name={props.icon} className={`inline-block`} size={16} />
        }
        title={t(props.title)}
      >
        {isOpen && (
          <FirstlineTableSalesColumnModal
            userId={props.userProfile.userId}
            businessDataPropertyName={props.businessDataPropertyName}
            close={close}
          />
        )}
      </ContentContainer>
    </>
  );
}

function FirstlineTableSalesColumnModal(props: {
  readonly userId: string;
  readonly businessDataPropertyName: keyof IUserTurnoverData;
  readonly close: () => void;
}) {
  const { data: businessData, isLoading } = useBusinessData({
    userId: props.userId,
    historicalMonths: 14
  });

  const { monthlyTrend, yearlyTrend, turnoverData } = useMemo(() => {
    const turnoverData = businessData?.[
      props.businessDataPropertyName
    ] as ITurnoverExtended;

    return {
      turnoverData,
      monthlyTrend:
        !_.isUndefined(turnoverData?.lastMonth) &&
        !_.isUndefined(turnoverData?.current)
          ? computePercentageTrend(turnoverData.current, turnoverData.lastMonth)
          : undefined,
      yearlyTrend:
        !_.isUndefined(turnoverData?.lastYear) &&
        !_.isUndefined(turnoverData?.current)
          ? computePercentageTrend(turnoverData.current, turnoverData.lastYear)
          : undefined
    };
  }, [businessData, props.businessDataPropertyName]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <LoadingSpinner smallSize />
      </div>
    );
  }

  if (_.isUndefined(turnoverData)) {
    return null;
  }

  return (
    <WidgetTurnoverDetails
      currentValue={turnoverData.current}
      monthlyTrend={monthlyTrend}
      yearlyTrend={yearlyTrend}
      lastMonth={turnoverData.lastMonth}
      lastYear={turnoverData.lastYear}
      historicalData={turnoverData.history}
    />
  );
}
